import { stripHtml } from 'core/utils/utils';
import ModuleItem from 'core/modules/components/lists/modules/ModuleItem/ModuleItem';
import React from 'react';
import { useToasts } from 'react-toast-notifications';

/**
 * ModulesList - Lista de submódulos/lecciones con semántica `<ul>/<li>` propia y una
 * acción principal accesible por item (overlay `<button>`). Los controles anidados
 * quedan por encima vía z-index para no anidar interactivos dentro del botón.
 */

// `ModuleItem` es JS (props sin tipar): se aliasa a un componente permisivo para poder
// inyectar las props de dominio de cada submódulo mediante spread.
const ModuleRow = ModuleItem as unknown as React.FC<Record<string, unknown>>;

export type Submodule = Readonly<{
  guid?: string;
  status?: string;
  is_accessible?: number;
  isNew?: boolean;
  lesson_name?: string;
  name?: string;
  image?: unknown;
  sessions?: unknown;
}> & Record<string, unknown>;

type ModulesListProps = Readonly<{
  contentLang?: string;
  enableOverlays?: boolean;
  isEdelvives?: boolean;
  isEditorial?: boolean;
  isFixedLayout?: boolean;
  isStudent?: boolean;
  moduleIndex?: number;
  modules?: unknown[];
  modulesSize?: number;
  onChangeModuleVisibility?: (...args: unknown[]) => void;
  onClickSubmodule?: (item: Submodule, sectionGuid?: string) => void;
  sectionGuid?: string;
  showOptionsInItems?: boolean;
  studentGradebook?: Record<string, unknown> | null;
  submodules?: Submodule[];
  t?: (key: string) => string;
  twoLevelProgram?: boolean;
}>;

function ModulesList({
  contentLang,
  enableOverlays = false,
  isEdelvives = false,
  isEditorial = false,
  isFixedLayout = false,
  isStudent,
  moduleIndex,
  modules = [],
  modulesSize,
  onChangeModuleVisibility,
  onClickSubmodule = () => {},
  sectionGuid,
  showOptionsInItems,
  studentGradebook = null,
  submodules = [],
  t = (key) => key,
  twoLevelProgram = false,
}: ModulesListProps): JSX.Element {
  const { addToast } = useToasts();

  function getIsAccessible(item: Submodule): boolean {
    if (!isFixedLayout) return true;
    return twoLevelProgram || isEditorial || item?.is_accessible === 1;
  }

  function onLesson(item: Submodule, currentSectionGuid?: string): void {
    if (!getIsAccessible(item)) return;
    if (!item.isNew) {
      onClickSubmodule(item, currentSectionGuid);
    } else {
      addToast(t('lesson:You must publish to see the new lesson'), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  return (
    <ul className="modules-list modules-list-in-edit-mode">
      {submodules.map((item, i) => (
        <li
          key={item.guid}
          className={`modules-list__item modules-list__item--${item.status ? item.status : 'pending'}${!getIsAccessible(item) ? ' modules-list__item--not-accessible' : ''}`}
        >
          {/* Acción principal accesible: cubre la tarjeta como overlay para abrir el
              submódulo. Los controles anidados (menú) quedan por encima vía z-index
              para no anidar interactivos dentro del botón. */}
          <button
            type="button"
            className="modules-list__item-overlay"
            aria-label={stripHtml(item.lesson_name || item.name || '') || undefined}
            disabled={!getIsAccessible(item)}
            onClick={() => onLesson(item, sectionGuid)}
          />
          <ModuleRow
            contentLang={contentLang}
            {...item}
            sessionInfo={item?.sessions}
            name={item.lesson_name || item.name}
            modules={modules}
            moduleIndex={moduleIndex}
            modulesSize={modulesSize}
            index={i + 1}
            size={submodules.length}
            showOptionsInItems={showOptionsInItems}
            item={item.image}
            onChangeModuleVisibility={onChangeModuleVisibility}
            isStudent={isStudent}
            studentGradebook={studentGradebook}
            enableOverlays={enableOverlays}
            twoLevelProgram={twoLevelProgram}
            isAccessible={getIsAccessible(item)}
            isEdelvives={isEdelvives}
            isEditorial={isEditorial}
          />
        </li>
      ))}
    </ul>
  );
}

export default ModulesList;
